import React, { useEffect, useState, useMemo, useCallback } from "react";
import "../Dashboard/dashboard.scss";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { useDispatch, useSelector } from "react-redux";
import FooterComponent from "components/LandingPageTemplateComponents/FooterComponent";
import { getBusinessAnalytics } from "api/dashboard/getBusinessAnalytics";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Same visual vocabulary as the Prigmar dashboard.
function StatCard({ label, value, accent = "#0F62FE", sub }) {
  return (
    <div className="dashboard-cards card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1.1 }}>
              {value == null ? "—" : value}
            </div>
            {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, height = 240 }) {
  return (
    <div className="card border-0 mb-3">
      <div className="card-body">
        <p className="chart-header mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
          {title}
        </p>
        <div style={{ width: "100%", height }}>{children}</div>
      </div>
    </div>
  );
}

// Render a {key:count} map as a simple labelled list (states, statuses…).
function BreakdownList({ rows, empty = "No data yet" }) {
  const entries = Object.entries(rows || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) {
    return <div style={{ fontSize: 13, color: "#94a3b8" }}>{empty}</div>;
  }
  return (
    <ul className="list-unstyled mb-0">
      {entries
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => (
          <li key={k} className="d-flex justify-content-between" style={{ fontSize: 13, padding: "2px 0" }}>
            <span style={{ color: "#475569", textTransform: "capitalize" }}>{String(k).replace(/[_-]/g, " ")}</span>
            <span style={{ fontWeight: 600, color: "#0f172a" }}>{v}</span>
          </li>
        ))}
    </ul>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// --- Pure transforms (unit-tested via Analytics._test) ---------------------
const humanizeLabel = (s) => String(s == null ? "" : s).replace(/[_-]/g, " ");

// API conversions.byDate → recharts line series.
const toConversionSeries = (conversions) =>
  ((conversions && conversions.byDate) || []).map((d) => ({
    date: d.date,
    conversions: d.count,
  }));

// API topIssues → recharts bar series with humanised labels.
const toIssuesSeries = (topIssues) =>
  (topIssues || []).map((i) => ({ name: humanizeLabel(i.label), count: i.count }));

export default function Analytics() {
  const dispatch = useDispatch();
  const userInfoRedux = useSelector((s) => s.user.userInfo);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState("");

  const fetchData = useCallback(async (cid) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBusinessAnalytics(cid || null);
      setData(res);
    } catch (e) {
      setError(e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(companyId || null);
  }, [fetchData, companyId]);

  const contacts = data?.contacts || {};
  const conversions = data?.conversions || { total: 0, byDate: [] };
  const lastComms = data?.lastCommunications || [];
  const messages = data?.messages || {};
  const chats = data?.chats || {};
  const communities = data?.communities || {};
  const platforms = data?.platforms || {};
  const socialPosts = data?.socialPosts || {};
  const sequences = data?.sequences || {};
  const topIssues = data?.topIssues || [];
  const topRequests = data?.topRequests || [];
  const stripe = data?.stripe || null;
  const companies = data?.companies || [];

  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const scopeLabel =
    data?.scope?.role === "superAdmin"
      ? data?.scope?.selectedCompanyId
        ? `Super admin · ${companies.find((c) => c._id === data.scope.selectedCompanyId)?.name || "selected company"}`
        : "Super admin · all companies"
      : data?.scope?.role === "companyAdmin"
        ? "Company admin · your company"
        : "Your brands";

  const conversionsData = useMemo(() => toConversionSeries(conversions), [conversions]);
  const issuesData = useMemo(() => toIssuesSeries(topIssues), [topIssues]);

  return (
    <div style={{ background: "#eaeaea" }}>
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <span
              style={{ alignItems: "center", display: "flex", cursor: "pointer" }}
              onClick={() => dispatch(setSidebarStatus(true))}
            >
              <div className="cursor-pointer">
                <img src={Arrow} className="left-arrow-img" alt="" />
              </div>
              <p className="my-heading f-14 ml-3 mr-3">Side Menu</p>
            </span>
          </div>
          <div className="projActionButtons d-flex flex-wrap" style={{ maxWidth: "100vw" }}>
            <div className="btn-tool-group">
              <button
                disabled={loading}
                onClick={() => fetchData(companyId || null)}
                className="px-3 bg-white text-bid shadow-hover"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary mx-3">
        <div className="h-screen flex-grow-1 overflow-y-lg-auto">
          <main className="py-6 bg-surface-secondary">
            <div className="container-fluid">
              {/* Scope banner + company picker */}
              <div className="row mt-3">
                <div className="col-12 d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 22 }}>Business analytics</h2>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {scopeLabel}
                      {data?.scope?.brandCount != null && (
                        <span> · {data.scope.brandCount} brand{data.scope.brandCount === 1 ? "" : "s"} in scope</span>
                      )}
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <label style={{ fontSize: 13, color: "#475569", margin: 0 }}>Filter by company</label>
                      <select
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        className="form-control"
                        style={{ width: 240 }}
                      >
                        <option value="">All companies</option>
                        {companies.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="alert alert-warning mt-3" role="alert" style={{ fontSize: 13 }}>
                  {error}
                </div>
              )}
              {loading && (
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 12 }}>Loading…</div>
              )}

              {/* --- Headline stat cards --- */}
              <div className="row mt-3" style={{ rowGap: 12 }}>
                <div className="col-6 col-md-3">
                  <StatCard label="Customers" value={contacts.customers} accent="#22c55e" sub={`${contacts.total || 0} contacts total`} />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Prospects" value={contacts.prospects} accent="#0F62FE" />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Conversions" value={conversions.total} accent="#a855f7" sub="purchase events" />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Messages sent" value={messages.sent} accent="#0ea5e9" sub={`${messages.replies || 0} replies`} />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Chats" value={chats.total} accent="#f59e0b" />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Social posts" value={socialPosts.posted} accent="#14b8a6" sub={`${socialPosts.total || 0} total`} />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Connected platforms" value={platforms.distinct} accent="#6366f1" sub={`${platforms.connectedAccounts || 0} accounts`} />
                </div>
                <div className="col-6 col-md-3">
                  <StatCard label="Communities" value={communities.total} accent="#ec4899" sub={`${communities.totalReplies || 0} replies sent`} />
                </div>
              </div>

              {/* --- Conversions over time --- */}
              <div className="row mt-3">
                <div className="col-12 col-lg-8">
                  <ChartCard title="Conversions by date">
                    {conversionsData.length > 0 ? (
                      <ResponsiveContainer>
                        <LineChart data={conversionsData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="conversions" stroke="#a855f7" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>No conversions recorded yet.</div>
                    )}
                  </ChartCard>
                </div>
                <div className="col-12 col-lg-4">
                  <ChartCard title="Marketing sequences" height={240}>
                    <div className="d-flex flex-column" style={{ gap: 10 }}>
                      <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <span style={{ color: "#475569" }}>Sequences created</span>
                        <strong>{sequences.created || 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <span style={{ color: "#475569" }}>Enrollments</span>
                        <strong>{sequences.enrollments || 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <span style={{ color: "#475569" }}>Emails sent</span>
                        <strong>{sequences.sent || 0}</strong>
                      </div>
                      <hr className="my-1" />
                      <BreakdownList rows={sequences.byStatus} empty="No sequences yet" />
                    </div>
                  </ChartCard>
                </div>
              </div>

              {/* --- Issues + Requests --- */}
              <div className="row">
                <div className="col-12 col-lg-7">
                  <ChartCard title="Issues mentioned the most">
                    {issuesData.length > 0 ? (
                      <ResponsiveContainer>
                        <BarChart data={issuesData} layout="vertical" margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>No classified issues yet.</div>
                    )}
                  </ChartCard>
                </div>
                <div className="col-12 col-lg-5">
                  <ChartCard title="Most-mentioned requests" height={240}>
                    {topRequests.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {topRequests.map((r, i) => (
                          <li key={i} className="d-flex justify-content-between" style={{ fontSize: 13, padding: "3px 0" }}>
                            <span style={{ color: "#475569" }}>{r.request}</span>
                            <span style={{ fontWeight: 600 }}>{r.count}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>No feature requests yet.</div>
                    )}
                  </ChartCard>
                </div>
              </div>

              {/* --- Breakdowns: customer states / prospect states / chat status --- */}
              <div className="row">
                <div className="col-12 col-md-4">
                  <ChartCard title="Customers by state" height={180}>
                    <BreakdownList rows={contacts.byCustomerState} empty="No customers yet" />
                  </ChartCard>
                </div>
                <div className="col-12 col-md-4">
                  <ChartCard title="Prospects by state" height={180}>
                    <BreakdownList rows={contacts.byProspectState} empty="No prospects yet" />
                  </ChartCard>
                </div>
                <div className="col-12 col-md-4">
                  <ChartCard title="Chats by status" height={180}>
                    <BreakdownList rows={chats.byStatus} empty="No chats yet" />
                  </ChartCard>
                </div>
              </div>

              {/* --- Communications + platforms --- */}
              <div className="row">
                <div className="col-12 col-lg-7">
                  <ChartCard title="Latest communications" height={300}>
                    {lastComms.length > 0 ? (
                      <div style={{ overflowY: "auto", height: "100%" }}>
                        <table className="table table-sm" style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Contact</th>
                              <th>Type</th>
                              <th>Last in</th>
                              <th>Last out</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lastComms.map((c) => (
                              <tr key={c._id}>
                                <td>{c.name || c.email}</td>
                                <td style={{ textTransform: "capitalize" }}>{c.type}</td>
                                <td>{fmtDate(c.lastInboundAt)}</td>
                                <td>{fmtDate(c.lastOutboundAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>No communications yet.</div>
                    )}
                  </ChartCard>
                </div>
                <div className="col-12 col-lg-5">
                  <ChartCard title="Connected platforms" height={300}>
                    <BreakdownList rows={platforms.byPlatform} empty="No platforms connected" />
                    {stripe && (
                      <div className="mt-3" style={{ fontSize: 13 }}>
                        <hr />
                        <div className="d-flex justify-content-between">
                          <span style={{ color: "#475569" }}>Stripe billing customers</span>
                          <strong>{stripe.customers}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{stripe.note}</div>
                      </div>
                    )}
                  </ChartCard>
                </div>
              </div>
            </div>
          </main>
          <FooterComponent />
        </div>
      </div>
    </div>
  );
}

// Pure helpers exposed for unit tests (recharts/render is untestable here).
Analytics._test = { humanizeLabel, toConversionSeries, toIssuesSeries, fmtDate };
